<template>
  <!--
    HeroCarousel Component
    ======================
    Purpose: Create an engaging carousel for the hero section with multiple slide designs

    Features:
    - 6 unique slides with different themes and fonts
    - Smooth fade transitions between slides
    - Chevron navigation controls
    - Dot indicators for slide position
    - Auto-play with pause on hover
    - Responsive design
    - Loading state
    - Error handling
    - Keyboard navigation

    Dependencies:
    - Swiper.js for carousel functionality
    - Custom fonts for unique typography per slide
  -->
  <div class="hero-carousel-wrapper" v-if="!error">
    <!-- Loading State -->
    <div v-if="loading" class="carousel-loading">
      <div class="loading-spinner"></div>
      <p class="loading-text">Loading experience...</p>
    </div>
    <!--
      Swiper Container
      The main carousel container that holds all slides
    -->
    <div class="swiper hero-carousel" ref="swiperContainer" v-show="!loading" role="region" aria-label="Hero carousel" aria-roledescription="carousel">
      <div class="swiper-wrapper" aria-live="polite">

        <!--
          Slide 1: World's First Professional Service
          Theme: Professional restoration with handwritten accent
          Font: Caveat for handwritten word, Inter for clean text
        -->
        <div class="swiper-slide slide-restoration">
          <div class="slide-content">
            <h1 class="slide-title restoration-title">
                <span class="title-handwritten">World's First</span>
                <span class="title-main">Professional Card Restoration Grading Company</span>
                <span class="title-sub">Trusted by Collectors Worldwide</span>
            </h1>
            <p class="slide-subtitle restoration-subtitle">
              Bring your valuable cards back to life with our expert restoration services
            </p>
            <div class="slide-buttons">
              <a href="/register" class="btn btn-primary">Start Your Submission</a>
              <a href="#features" class="btn btn-outline">Learn More</a>
            </div>
          </div>
        </div>

        <!--
          Slide 2: Professional Service Excellence
          Theme: Before/after transformation focus
          Font: Permanent Marker for handwritten accent
        -->
        <div class="swiper-slide slide-transform">
          <div class="slide-content">
            <h1 class="slide-title transform-title">
              <span class="title-handwritten">Fascinating</span>
              <span class="title-main">Card Restoration Transformation</span>
            </h1>
            <p class="slide-subtitle transform-subtitle">
              Industry-leading techniques only possible by our team of experts
            </p>
            <div class="slide-buttons">
              <a href="/register" class="btn btn-primary">Start Your Submission</a>
              <a href="/howitworks" class="btn btn-outline">See Our Process</a>
            </div>
            <div class="transform-badges">
              <span class="badge">✓ High Success Rate</span>
              <span class="badge">✓ 2-Week Turnaround</span>
              <span class="badge">✓ Insured Shipping</span>
            </div>
          </div>
        </div>

        <!--
          Slide 3: Scientific Restoration Method
          Theme: Conservation and value preservation
          Font: Kalam for handwritten style
        -->
        <div class="swiper-slide slide-preserve">
          <div class="slide-content">
            <h1 class="slide-title preserve-title">
              <span class="title-handwritten">Scientific</span>
              <span class="title-main">Restoration Methodology</span>
            </h1>
            <p class="slide-subtitle preserve-subtitle">
              Museum-grade conservation techniques adapted for Pokemon card preservation
            </p>
            <div class="slide-value-props">
              <div class="value-item">
                <svg class="value-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
                <span class="value-text">Fast Turnaround</span>
              </div>
              <div class="value-item">
                <svg class="value-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M9 11L12 14L22 4"/>
                  <path d="M21 12V19C21 20 20 21 19 21H5C4 21 3 20 3 19V5C3 4 4 3 5 3H16"/>
                </svg>
                <span class="value-text">Live Updates</span>
              </div>
              <div class="value-item">
                <svg class="value-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                <span class="value-text">Top Reviews</span>
              </div>
            </div>
            <div class="slide-buttons">
              <a href="/register" class="btn btn-primary">Get Started</a>
              <a href="/pricing" class="btn btn-outline">See Pricing</a>
            </div>
          </div>
        </div>

        <!--
          Slide 4: Expert Team
          Theme: Professional expertise
          Font: Indie Flower for playful handwritten style
        -->
        <div class="swiper-slide slide-revive">
          <div class="slide-content">
            <h1 class="slide-title revive-title">
              <span class="title-handwritten">Expert</span>
              <span class="title-main">Restoration Specialists</span>
            </h1>
            <p class="slide-subtitle revive-subtitle">
              Our certified technicians provide expert restoration for all trading cards including sports & TCG.
            </p>
            <div class="slide-buttons">
              <a href="/register" class="btn btn-primary">Sign Up Now</a>
              <a href="#testimonials" class="btn btn-outline">Read Reviews</a>
            </div>
            <!-- Removed background SVG decorations -->
          </div>
        </div>

        <!--
          Slide 5: Guaranteed Results
          Theme: Quality assurance and guarantees
          Font: Amatic SC for urgent handwritten feel
        -->
        <div class="swiper-slide slide-rescue">
          <div class="slide-content">
            <h1 class="slide-title rescue-title">
              <span class="title-handwritten">Guaranteed</span>
              <span class="title-main">Restoration Results</span>
            </h1>
            <p class="slide-subtitle rescue-subtitle">
              100% satisfaction guarantee or your money back - we stand behind our work
            </p>
            <div class="slide-urgent">
              <span class="urgent-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M9 11L12 14L22 4"/>
                  <path d="M21 12V19C21 20 20 21 19 21H5C4 21 3 20 3 19V5C3 4 4 3 5 3H16"/>
                </svg>
                Satisfaction Guaranteed
              </span>
              <span class="urgent-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2L2 7V12C2 16.5 4.5 20.74 12 22C19.5 20.74 22 16.5 22 12V7L12 2Z"/>
                </svg>
                Fully Insured
              </span>
            </div>
            <div class="slide-buttons">
              <a href="/register" class="btn btn-primary">Get Started Risk-Free</a>
              <a href="/contact" class="btn btn-outline">Ask Questions</a>
            </div>
          </div>
        </div>

        <!--
          Slide 6: Industry Leader
          Theme: Premium service for serious collectors
          Font: Dancing Script for elegant handwritten style
        -->
        <div class="swiper-slide slide-perfect">
          <div class="slide-content">
            <h1 class="slide-title perfect-title">
              <span class="title-handwritten">Ethical</span>
              <span class="title-main">Restoration Methods</span>
            </h1>
            <p class="slide-subtitle perfect-subtitle">
                We use ethical restoration methods to ensure your cards are restored to their best condition without coloring, trimming or rebacking.
            </p>
            <div class="slide-stats">
              <div class="stat-item">
                <span class="stat-number">5,000+</span>
                <span class="stat-label">Cards Restored</span>
              </div>
              <div class="stat-divider">•</div>
              <div class="stat-item">
                <span class="stat-number">1,500+</span>
                <span class="stat-label">Happy Customers</span>
              </div>
            </div>
            <div class="slide-buttons">
              <a href="/register" class="btn btn-primary">
                <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                <span>Join Today</span>
              </a>
              <a href="/pricing" class="btn btn-outline">View Services</a>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!--
      Navigation Row
      Responsive bottom navigation with left/right buttons and slide indicators
    -->
    <div class="carousel-navigation-row">
      <!-- Left navigation button -->
      <button class="nav-button nav-button-prev" @click="slidePrev" aria-label="Previous slide">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M15 18l-6-6 6-6"/>
        </svg>
      </button>

      <!-- Slide indicators (pagination) -->
      <div class="swiper-pagination"></div>

      <!-- Right navigation button -->
      <button class="nav-button nav-button-next" @click="slideNext" aria-label="Next slide">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </button>
    </div>
  </div>

  <!-- Error Fallback -->
  <div v-else class="carousel-error">
    <div class="error-content">
      <h2 class="error-title">
          <span class="title-accent">World's First</span>
          <span class="title-main">Professional Card Restoration Grading Company</span>
      </h2>
      <p class="error-subtitle">Trusted by Collectors Worldwide</p>
      <div class="error-buttons">
        <a href="/register" class="btn btn-primary">Start Your Submission</a>
        <a href="#features" class="btn btn-outline">Learn More</a>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
// Import only necessary Swiper modules for smaller bundle size
import Swiper from 'swiper'
import { Navigation, Pagination, Autoplay, EffectFade, Keyboard, A11y } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-fade'

export default {
  name: 'HeroCarousel',

  setup() {
    /**
     * Component State
     * ===============
     */
    const swiperContainer = ref(null)
    const loading = ref(true)
    const error = ref(false)
    let swiperInstance = null
    let initAttempts = 0
    const maxAttempts = 3

    /**
     * Initialize Swiper Carousel
     * ==========================
     * Configure carousel with fade effect and auto-play
     */
    const initSwiper = () => {
      initAttempts++

      if (!swiperContainer.value) {
        // Swiper container not found, retry
        if (initAttempts < maxAttempts) {
          setTimeout(() => initSwiper(), 500)
        } else {
          error.value = true
          loading.value = false
        }
        return
      }

      // Initialize Swiper carousel

      try {
        // Check if Swiper is available
        if (typeof Swiper === 'undefined') {
          throw new Error('Swiper library not loaded')
        }

        swiperInstance = new Swiper(swiperContainer.value, {
        // Specify only the modules we need
        modules: [Navigation, Pagination, Autoplay, EffectFade, Keyboard, A11y],

        // Fade effect for smooth transitions
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },

        // Auto-play configuration
        autoplay: {
          delay: 10000, // 5 seconds per slide
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        },

        // Loop through slides infinitely
        loop: true,

        // Touch/swipe settings
        touchEventsTarget: 'container',
        allowTouchMove: true,
        touchRatio: 1,
        touchAngle: 45,
        simulateTouch: true,
        grabCursor: true,

        // Navigation arrows (controlled by our custom buttons)
        navigation: false, // We'll use custom navigation

        // Pagination dots
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
          renderBullet: function (index, className) {
            // Custom bullet design - rounded lines
            return `<span class="${className} custom-bullet"></span>`
          }
        },

        // Animation speed
        speed: 800,

        // Keyboard navigation
        keyboard: {
          enabled: true,
          onlyInViewport: true
        },

        // Accessibility
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
          firstSlideMessage: 'This is the first slide',
          lastSlideMessage: 'This is the last slide',
          paginationBulletMessage: 'Go to slide {{index}}'
        },

        // Callbacks for animations
        on: {
          slideChangeTransitionStart: function() {
            // Add animation classes when slide changes
            const activeSlide = this.slides[this.activeIndex]
            if (activeSlide && activeSlide.classList) {
              activeSlide.classList.add('animating')
            }
          },
          slideChangeTransitionEnd: function() {
            // Remove animation classes after transition
            if (this.slides && this.slides.forEach) {
              this.slides.forEach(slide => {
                if (slide && slide.classList) {
                  slide.classList.remove('animating')
                }
              })
            }
          },
          init: function() {
            loading.value = false
          },
          beforeDestroy: function() {
            // Cleanup handled in destroy
          }
        }
      })
      } catch (err) {
        // Failed to initialize Swiper
        error.value = true
        loading.value = false
      }
    }

    /**
     * Navigation Methods
     * ==================
     */
    const slidePrev = () => {
      if (swiperInstance) {
        swiperInstance.slidePrev()
      }
    }

    const slideNext = () => {
      if (swiperInstance) {
        swiperInstance.slideNext()
      }
    }

    /**
     * Lifecycle Hooks
     * ===============
     */
    onMounted(() => {
      // Initialize Swiper on mount
      loading.value = true
      // Initialize carousel after component mounts
      // Use nextTick to ensure DOM is ready
      nextTick(() => {
        setTimeout(() => {
          initSwiper()
        }, 100)
      })
    })

    onUnmounted(() => {
      // Cleanup to prevent memory leaks
      if (swiperInstance) {
        // Destroy Swiper instance
        swiperInstance.destroy(true, true)
        swiperInstance = null
      }
      // Clear any remaining timeouts
      loading.value = false
      error.value = false
    })

    return {
      swiperContainer,
      slidePrev,
      slideNext,
      loading,
      error
    }
  }
}
</script>

<style scoped>
/**
 * Hero Carousel Styles
 * ====================
 * Comprehensive styling for the carousel component
 */

/* Import Google Fonts for different slide themes - including handwritten styles */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800&family=Pacifico&family=Playfair+Display:wght@400;700&family=Dancing+Script:wght@700&family=Caveat:wght@400;700&family=Kalam:wght@400;700&family=Permanent+Marker&family=Indie+Flower&family=Amatic+SC:wght@400;700&display=swap');

/**
 * CSS Variables for Consistent Theming
 * =====================================
 */
.hero-carousel-wrapper {
  /* Text Colors */
  --carousel-text-primary: #ffffff;
  --carousel-text-secondary: rgba(255, 255, 255, 0.95);
  --carousel-text-tertiary: rgba(255, 255, 255, 0.85);
  --carousel-text-muted: rgba(255, 255, 255, 0.7);
  --carousel-text-light: rgba(255, 255, 255, 0.5);
  --carousel-text-faint: rgba(255, 255, 255, 0.3);

  /* Background Colors */
  --carousel-bg-overlay: rgba(255, 255, 255, 0.1);
  --carousel-bg-overlay-hover: rgba(255, 255, 255, 0.2);
  --carousel-bg-glass: rgba(255, 255, 255, 0.15);

  /* Border Colors */
  --carousel-border-light: rgba(255, 255, 255, 0.2);
  --carousel-border-medium: rgba(255, 255, 255, 0.3);
  --carousel-border-focus: rgba(255, 255, 255, 0.8);

  /* Shadow Styles */
  --carousel-shadow-sm: 2px 2px 4px rgba(0, 0, 0, 0.2);
  --carousel-shadow-md: 2px 2px 6px rgba(0, 0, 0, 0.3);
  --carousel-shadow-lg: 2px 2px 8px rgba(0, 0, 0, 0.3);
  --carousel-shadow-xl: 3px 3px 10px rgba(0, 0, 0, 0.3);
  --carousel-shadow-hard: 3px 3px 0 rgba(0, 0, 0, 0.3);
}

/* Essential Swiper Styles */
.swiper {
  margin-left: auto;
  margin-right: auto;
  position: relative;
  overflow: hidden;
  list-style: none;
  padding: 0;
  z-index: 1;
}

.swiper-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 1;
  display: flex;
  transition-property: transform;
  box-sizing: content-box;
}

.swiper-slide {
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  position: relative;
  transition-property: opacity;
}

/**
 * Container Styles
 * ================
 */
.hero-carousel-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 10;
}

.hero-carousel {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.swiper-slide {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.8s ease;
  /* Performance optimizations */
  will-change: opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
}

.swiper-slide-active {
  opacity: 1;
}

.slide-content {
  text-align: center;
  max-width: 750px; /* Reduced from 900px */
  width: 100%;
  padding: 1rem 1rem 2.5rem; /* Further reduced padding */
  position: relative;
}

/**
 * Navigation Row
 * ==============
 * Bottom navigation overlay with buttons and indicators
 */
.carousel-navigation-row {
  position: absolute;
  bottom: 1rem;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 1.5rem;
  z-index: 20;
}

/**
 * Navigation Buttons
 * ==================
 * Left and right navigation buttons in the bottom row
 */
.nav-button {
  background: var(--carousel-bg-overlay);
  border: 1px solid var(--carousel-border-light);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: var(--carousel-text-primary);
  flex-shrink: 0;
  /* Prevent layout shifts */
  will-change: transform;
  backface-visibility: hidden;
}

.nav-button:hover {
  background: var(--carousel-bg-overlay-hover);
  transform: scale(1.1);
  border-color: var(--carousel-border-medium);
}

.nav-button:active {
  transform: scale(0.95);
}

.nav-button svg {
  width: 20px;
  height: 20px;
}

/**
 * Pagination Dots (Rounded Lines)
 * ================================
 */
.carousel-navigation-row .swiper-pagination {
  position: relative !important;
  bottom: auto !important;
  left: auto !important;
  transform: none !important;
  display: flex !important;
  gap: 8px;
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 0 1rem;
}

.swiper-pagination ::v-deep(.custom-bullet) {
  width: 24px;
  height: 4px;
  border-radius: 2px;
  background: var(--carousel-border-medium);
  opacity: 1 !important;
  transition: all 0.3s ease;
  cursor: pointer;
}

.swiper-pagination ::v-deep(.custom-bullet.swiper-pagination-bullet-active) {
  width: 40px;
  background: var(--carousel-text-secondary);
}

/**
 * Slide 1: Restoration Theme
 * ============================
 */
.slide-restoration .restoration-title {
  margin-bottom: 1.5rem;
}

.slide-restoration .title-handwritten {
  display: block;
  font-family: 'Caveat', cursive;
  font-size: clamp(3rem, 6vw, 4.5rem); /* Reduced from 5.5rem */
  font-weight: 700;
  color: var(--carousel-text-primary);
  transform: rotate(-3deg);
  margin-bottom: -0.5rem;
  animation: handwrittenIn 0.8s ease-out;
}

.slide-restoration .title-main {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.75rem, 3.5vw, 2.5rem); /* Reduced from 3rem */
  font-weight: 700;
  color: var(--carousel-text-secondary);
  margin: 0.5rem 0;
  animation: fadeInUp 0.8s ease-out 0.2s backwards;
}

.slide-restoration .title-sub {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.25rem, 2.5vw, 1.8rem); /* Reduced from 2.2rem */
  font-weight: 400;
  color: var(--carousel-text-tertiary);
  animation: fadeInUp 0.8s ease-out 0.3s backwards;
}

.slide-restoration .restoration-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: clamp(1rem, 2vw, 1.2rem); /* Reduced from 1.4rem */
  color: var(--carousel-text-tertiary);
  margin-bottom: 2rem; /* Reduced from 2.5rem */
  animation: fadeIn 0.8s ease-out 0.4s backwards;
}

/**
 * Slide 2: Transform Theme
 * =======================
 */
.slide-transform .transform-title {
  margin-bottom: 1.5rem;
}

.slide-transform .title-handwritten {
  display: block;
  font-family: 'Permanent Marker', cursive;
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: var(--carousel-text-primary);
  transform: rotate(-2deg) skew(-5deg);
  text-shadow: var(--carousel-shadow-hard);
  margin-bottom: -0.3rem;
  animation: markerIn 0.8s ease-out;
}

.slide-transform .title-main {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.5rem, 3vw, 2.3rem);
  font-weight: 600;
  color: var(--carousel-text-secondary);
  margin-top: 0.5rem;
  animation: fadeInUp 0.8s ease-out 0.2s backwards;
}

.slide-transform .transform-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: clamp(1rem, 2.2vw, 1.3rem);
  color: var(--carousel-text-tertiary);
  margin-bottom: 2rem;
  animation: fadeIn 0.8s ease-out 0.3s backwards;
}

.transform-badges {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
  flex-wrap: wrap;
  animation: fadeInUp 0.8s ease-out 0.5s backwards;
}

.transform-badges .badge {
  background: var(--carousel-bg-overlay);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  color: var(--carousel-text-tertiary);
  border: 1px solid var(--carousel-border-light);
}

/* Removed gradient button styles - using standard btn-primary and btn-outline */

/**
 * Slide 3: Preserve Theme
 * =======================
 */
.slide-preserve .preserve-title {
  margin-bottom: 1.5rem;
}

.slide-preserve .title-handwritten {
  display: block;
  font-family: 'Kalam', cursive;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  color: var(--carousel-text-primary);
  transform: rotate(2deg);
  margin-bottom: -0.5rem;
  animation: handwrittenIn 0.8s ease-out;
}

.slide-preserve .title-main {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.75rem, 3.5vw, 2.5rem);
  font-weight: 600;
  color: var(--carousel-text-secondary);
  margin-top: 0.5rem;
  animation: fadeInUp 0.8s ease-out 0.2s backwards;
}

.slide-preserve .preserve-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: clamp(1rem, 2.2vw, 1.3rem);
  color: var(--carousel-text-tertiary);
  margin-bottom: 2rem;
  animation: fadeIn 0.8s ease-out 0.3s backwards;
}

.slide-value-props {
  display: flex;
  gap: 2rem;
  justify-content: center;
  margin-bottom: 2rem;
  animation: fadeInUp 0.8s ease-out 0.4s backwards;
}

.value-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.value-icon {
  width: 2rem;
  height: 2rem;
  color: var(--carousel-text-muted);
}

.value-text {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: var(--carousel-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

/* Removed preserve button styles - using standard btn-primary and btn-outline */

/**
 * Slide 4: Revive Theme
 * =======================
 */
.slide-revive .revive-title {
  margin-bottom: 1.5rem;
}

.slide-revive .title-handwritten {
  display: block;
  font-family: 'Indie Flower', cursive;
  font-size: clamp(3rem, 6vw, 4.5rem);
  color: var(--carousel-text-primary);
  transform: rotate(-4deg);
  text-shadow: var(--carousel-shadow-sm);
  margin-bottom: -0.8rem;
  animation: bounceIn 0.8s ease-out;
}

.slide-revive .title-main {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.75rem, 3.5vw, 2.5rem);
  font-weight: 600;
  color: var(--carousel-text-secondary);
  margin-top: 0.5rem;
  animation: fadeInUp 0.8s ease-out 0.2s backwards;
}

.slide-revive .revive-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: clamp(1rem, 2.2vw, 1.3rem);
  color: var(--carousel-text-tertiary);
  margin-bottom: 2rem;
  animation: fadeIn 0.8s ease-out 0.3s backwards;
}

/* Removed nostalgic-elements styles */

/* Removed nostalgia button styles - using standard btn-primary and btn-outline */

/**
 * Slide 5: Rescue Theme
 * =======================
 */
.slide-rescue .rescue-title {
  margin-bottom: 1.5rem;
}

.slide-rescue .title-handwritten {
  display: block;
  font-family: 'Amatic SC', cursive;
  font-size: clamp(3.5rem, 7vw, 5rem);
  font-weight: 700;
  color: var(--carousel-text-primary);
  transform: rotate(1deg) scale(1.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: -1rem;
  animation: urgentPulse 0.8s ease-out;
}

.slide-rescue .title-main {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.5rem, 3vw, 2.3rem);
  font-weight: 700;
  color: var(--carousel-text-secondary);
  margin-top: 0.5rem;
  animation: fadeInUp 0.8s ease-out 0.2s backwards;
}

.slide-rescue .rescue-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: clamp(1rem, 2.2vw, 1.3rem);
  color: var(--carousel-text-tertiary);
  margin-bottom: 1.5rem;
  animation: fadeIn 0.8s ease-out 0.3s backwards;
}

.slide-urgent {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 1.5rem;
  animation: fadeInUp 0.8s ease-out 0.4s backwards;
}

.urgent-badge {
  background: var(--carousel-bg-overlay);
  border: 1px solid var(--carousel-border-light);
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.9rem;
  color: var(--carousel-text-tertiary);
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.urgent-badge svg {
  width: 16px;
  height: 16px;
}

/* Removed emergency button styles - using standard btn-primary and btn-outline */

/**
 * Slide 6: Perfect Theme
 * =======================
 */
.slide-perfect .perfect-title {
  margin-bottom: 1.5rem;
}

.slide-perfect .title-handwritten {
  display: block;
  font-family: 'Dancing Script', cursive;
  font-size: clamp(3rem, 6vw, 4.5rem);
  font-weight: 700;
  color: var(--carousel-text-primary);
  transform: rotate(-3deg);
  margin-bottom: -0.5rem;
  animation: fadeInScale 0.8s ease-out;
}

.slide-perfect .title-main {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.5rem, 3vw, 2.3rem);
  font-weight: 600;
  color: var(--carousel-text-secondary);
  margin-top: 0.5rem;
  animation: fadeInUp 0.8s ease-out 0.2s backwards;
}

.slide-perfect .perfect-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: clamp(1rem, 2.2vw, 1.3rem);
  color: var(--carousel-text-tertiary);
  margin-bottom: 2rem;
  animation: fadeIn 0.8s ease-out 0.3s backwards;
}

.slide-stats {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
  animation: fadeInUp 1s ease-out 0.6s backwards;
}

.stat-item {
  text-align: center;
}

.stat-number {
  display: block;
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--carousel-text-primary);
}

.stat-label {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: var(--carousel-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-top: 0.25rem;
}

.stat-divider {
  color: var(--carousel-text-light);
  font-size: 1.5rem;
}

/* Removed premium button styles - using standard btn-primary and btn-outline */

/**
 * Common Button Styles
 * ====================
 */
.slide-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  animation: fadeInUp 0.8s ease-out 0.6s backwards;
}

.btn {
  padding: 12px 32px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: var(--carousel-text-primary);
  border: none;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.6);
}

.btn-outline {
  background: transparent;
  color: var(--carousel-text-primary);
  border: 2px solid var(--carousel-border-medium);
  backdrop-filter: blur(5px);
}

.btn-outline:hover {
  background: var(--carousel-bg-overlay);
  border-color: var(--carousel-text-light);
}

.btn-icon {
  width: 20px;
  height: 20px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 0.5rem;
}

/**
 * Animations
 * ===========
 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes sparkle {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.2);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  33% {
    transform: translateY(-20px) rotate(5deg);
  }
  66% {
    transform: translateY(10px) rotate(-5deg);
  }
}

@keyframes shimmer {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes handwrittenIn {
  0% {
    opacity: 0;
    transform: rotate(-3deg) scale(0.8);
  }
  50% {
    transform: rotate(-3deg) scale(1.1);
  }
  100% {
    opacity: 1;
    transform: rotate(-3deg) scale(1);
  }
}

@keyframes markerIn {
  0% {
    opacity: 0;
    transform: rotate(-2deg) skew(-5deg) translateX(-50px);
  }
  100% {
    opacity: 1;
    transform: rotate(-2deg) skew(-5deg) translateX(0);
  }
}

@keyframes twinkle {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.8);
  }
}

@keyframes urgentPulse {
  0% {
    transform: rotate(1deg) scale(0.9);
    opacity: 0;
  }
  50% {
    transform: rotate(1deg) scale(1.15);
  }
  100% {
    transform: rotate(1deg) scale(1.1);
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    box-shadow: var(--carousel-shadow-lg);
  }
  50% {
    box-shadow: var(--carousel-shadow-xl);
  }
  100% {
    box-shadow: var(--carousel-shadow-lg);
  }
}

/**
 * Responsive Design
 * ==================
 */
@media (max-width: 768px) {
  .carousel-navigation-row {
    padding: 0 1rem;
    gap: 0.75rem;
    bottom: 0.75rem;
  }

  .nav-button {
    width: 36px;
    height: 36px;
  }

  .nav-button svg {
    width: 18px;
    height: 18px;
  }

  .carousel-navigation-row .swiper-pagination {
    padding: 0 0.5rem;
  }

  .slide-content {
    padding: 1rem 1rem 3rem;
    width: 100%;
  }

  .slide-buttons {
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 0 1rem;
  }

  .slide-buttons .btn,
  .slide-buttons a {
    width: 100%;
    max-width: 280px;
    margin: 0 !important;
    font-size: 0.95rem;
    padding: 12px 24px;
  }

  .slide-stats {
    flex-direction: column;
    gap: 1rem;
  }

  .stat-divider {
    display: none;
  }

  .transform-badges {
    flex-wrap: wrap;
    justify-content: center;
  }

  .transform-badges .badge {
    font-size: 0.8rem;
    padding: 6px 12px;
  }

  .slide-value-props {
    flex-direction: column;
    gap: 1rem;
  }

  .slide-urgent {
    flex-direction: column;
    gap: 0.5rem;
  }

  .nostalgic-icon {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .carousel-navigation-row {
    bottom: 0.5rem;
  }

  .swiper-pagination ::v-deep(.custom-bullet) {
    width: 20px;
    height: 3px;
  }

  .swiper-pagination ::v-deep(.custom-bullet.swiper-pagination-bullet-active) {
    width: 32px;
  }

  .slide-content {
    padding: 1rem 0.75rem 2.5rem;
  }

  .slide-title {
    margin-bottom: 1rem;
  }

  .slide-subtitle {
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
  }

  .stat-number {
    font-size: 1.8rem;
  }

  .stat-label {
    font-size: 0.75rem;
  }

  .slide-value-props .value-icon {
    font-size: 1.5rem;
  }

  .slide-value-props .value-text {
    font-size: 0.8rem;
  }

  /* Hide chevrons on very small screens */
  .carousel-nav {
    display: none;
  }
}

/* Ensure animations work with Swiper's fade effect */
.swiper-slide-active .slide-content > * {
  animation-fill-mode: both;
}

/* Reset animations when slide is not active */
.swiper-slide:not(.swiper-slide-active) .slide-content > * {
  animation: none !important;
  opacity: 0;
}

/**
 * Loading State
 * =============
 */
.carousel-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 350px;
  color: white;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid var(--carousel-border-light);
  border-top-color: var(--carousel-text-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.loading-text {
  font-size: 1.1rem;
  color: var(--carousel-text-tertiary);
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/**
 * Error Fallback
 * ==============
 */
.carousel-error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 350px;
  width: 100%;
  text-align: center;
  padding: 2rem;
}

.error-content {
  max-width: 800px;
  width: 100%;
}

.error-title {
  margin-bottom: 1.5rem;
  color: white;
}

.error-title .title-accent {
  display: block;
  font-family: 'Caveat', cursive;
  font-size: clamp(3rem, 6vw, 4.5rem);
  font-weight: 700;
  color: var(--carousel-text-primary);
  transform: rotate(-3deg);
  margin-bottom: -0.5rem;
  text-shadow: var(--carousel-shadow-lg);
}

.error-title .title-main {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 700;
  margin-top: 0.5rem;
}

.error-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: clamp(1rem, 2.5vw, 1.3rem);
  color: var(--carousel-text-tertiary);
  margin-bottom: 2rem;
}

.error-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.error-buttons .btn {
  min-width: 150px;
}

/**
 * Focus Styles
 * ============
 */
.nav-button:focus-visible,
.swiper-pagination-bullet:focus-visible {
  outline: 2px solid var(--carousel-border-focus);
  outline-offset: 2px;
}

/**
 * Reduced Motion
 * ==============
 */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  .swiper-wrapper {
    transition: none !important;
  }
}
</style>
